import React, { useState, useEffect } from "react";
import { Alert, Button, Spinner, Row, Col } from "react-bootstrap";
import {
  getPaymentMethods,
  setDefaultPaymentMethod,
  deletePaymentMethod,
} from "../api";
import PaymentMethodCard from "./PaymentMethodCard";
import { useSubscription } from "../hooks/useSubscription";

/**
 * PaymentMethodList Component
 * Issue #11: Payment Method Management
 *
 * Displays list of user's payment methods with management actions
 */
const PaymentMethodList = ({
  onAddPaymentMethod,
  onRefresh,
  className = "",
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { refreshSubscription } = useSubscription();

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getPaymentMethods();

      if (response.data.success) {
        setPaymentMethods(response.data.payment_methods || []);
      } else {
        throw new Error(
          response.data.message || "Failed to load payment methods"
        );
      }
    } catch (err) {
      console.error("Error fetching payment methods:", err);
      setError(
        err.response?.data?.message ||
          "Failed to load payment methods. Please try again."
      );
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      setActionLoading(true);
      const response = await setDefaultPaymentMethod(paymentMethodId);

      if (response.data.success) {
        // Update local state to reflect the change
        setPaymentMethods((prev) =>
          prev.map((pm) => ({
            ...pm,
            is_default: pm.id === paymentMethodId,
          }))
        );

        // Refresh subscription data to update any cached payment method info
        await refreshSubscription();

        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(
          response.data.message || "Failed to set default payment method"
        );
      }
    } catch (err) {
      console.error("Error setting default payment method:", err);
      setError(
        err.response?.data?.message ||
          "Failed to set default payment method. Please try again."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (paymentMethodId) => {
    try {
      setActionLoading(true);
      const response = await deletePaymentMethod(paymentMethodId);

      if (response.data.success) {
        // Remove the deleted payment method from local state
        setPaymentMethods((prev) =>
          prev.filter((pm) => pm.id !== paymentMethodId)
        );

        // Refresh subscription data
        await refreshSubscription();

        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(
          response.data.message || "Failed to delete payment method"
        );
      }
    } catch (err) {
      console.error("Error deleting payment method:", err);
      setError(
        err.response?.data?.message ||
          "Failed to delete payment method. Please try again."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = (paymentMethodId) => {
    // This could open a detailed view modal or navigate to a details page
    console.log("View details for payment method:", paymentMethodId);
    // For now, we'll just log it. In a full implementation, this could:
    // - Open a modal with detailed payment method information
    // - Navigate to a dedicated payment method details page
    // - Show usage statistics and transaction history
  };

  const handleRefresh = () => {
    fetchPaymentMethods();
    if (onRefresh) {
      onRefresh();
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  if (loading) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading payment methods...</span>
        </Spinner>
        <div className="mt-2 text-muted">Loading payment methods...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <Alert
          variant="danger"
          className="d-flex justify-content-between align-items-center"
        >
          <div>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
          <Button variant="outline-danger" size="sm" onClick={handleRefresh}>
            <i className="fas fa-retry me-1"></i>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  if (paymentMethods.length === 0) {
    return (
      <div className={`text-center py-5 ${className}`}>
        <div className="mb-3">
          <i
            className="fas fa-credit-card text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
        </div>
        <h5 className="text-muted">No Payment Methods</h5>
        <p className="text-muted">
          You haven't added any payment methods yet. Add one to manage your
          subscription payments.
        </p>
        {onAddPaymentMethod && (
          <Button variant="primary" onClick={onAddPaymentMethod}>
            <i className="fas fa-plus me-2"></i>
            Add Payment Method
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          <i className="fas fa-credit-card me-2"></i>
          Payment Methods ({paymentMethods.length})
        </h6>
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={actionLoading}
            className="me-2"
          >
            <i className="fas fa-sync-alt me-1"></i>
            Refresh
          </Button>
          {onAddPaymentMethod && (
            <Button
              variant="primary"
              size="sm"
              onClick={onAddPaymentMethod}
              disabled={actionLoading}
            >
              <i className="fas fa-plus me-1"></i>
              Add New
            </Button>
          )}
        </div>
      </div>

      <Row>
        {paymentMethods.map((paymentMethod) => (
          <Col key={paymentMethod.id} md={6} lg={4} className="mb-3">
            <PaymentMethodCard
              paymentMethod={paymentMethod}
              isDefault={paymentMethod.is_default}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              onUpdate={handleViewDetails}
              showActions={true}
            />
          </Col>
        ))}
      </Row>

      {actionLoading && (
        <Alert variant="info" className="mt-3">
          <div className="d-flex align-items-center">
            <Spinner size="sm" className="me-2" />
            <span>Processing payment method changes...</span>
          </div>
        </Alert>
      )}
    </div>
  );
};

export default PaymentMethodList;
